import Link from 'next/link'
import { ContactVariables } from '../../src/GlobalVariables/Contact'
import { VehicleVariables } from '../../src/GlobalVariables/Vehicle'

export const carFinanceCalculatorText = [
  {
    mainHeading: 'How to use our car finance calculator',
    subHeadings: [
      {
        heading: 'Set your monthly budget',
        content: [
          'If you know what you can afford each month, put this as your monthly budget. By putting down a deposit using a lump sum, you can pay less each month for your car finance repayments.'
        ]
      },
      {
        heading: "How long you'd like to repay the loan",
        content: [
          "If you choose to spread your car finance over a longer period, it can make your monthly repayments lower. However, by opting for a longer repayment period, your total repayments will be more due to interest. Have a play with the slider on our calculator and you'll see how much your payments will be each month."
        ]
      },
      {
        heading: 'Your overall credit rating',
        content: [
          "Your credit rating can affect the rate you're offered by a lender. An excellent rating can result in a lower rate being offered, while a bad credit score could mean you're offered a high rate which will make your repayments higher. Our car finance calculator factors this in when working out what you can afford. If you don't know your credit score, you can see a guide on our finance calculator, or you can find it out for free using various companies such as Experian or ClearScore."
        ]
      }
    ]
  }
]

export const whatCanIDoNextData = {
  introText:
    'Now that you know how much you might be able to borrow, the next steps are up to you. You can:',
  items: [
    {
      subHeading: 'Find out more about car finance',
      content: (
        <>
          <p>
            We have lots of information about the different&nbsp;
            <Link href="https://www.zuto.com/car-finance-explained/" role="link">
              car finance options
            </Link>
            , as well as details on the most popular types, including&nbsp;
            <Link href="https://www.zuto.com/hp-car-finance/" role="link">
              HP car finance
            </Link>
            ,&nbsp;
            <Link href="https://www.zuto.com/pcp-car-finance/" role="link">
              PCP finance
            </Link>
            &nbsp;and Personal loans.
          </p>
        </>
      )
    },
    {
      subHeading: 'Improve your credit score',
      content: (
        <>
          <p>
            Even if you have a low credit score, lenders are often still able to help you.&nbsp;
            <Link href="https://www.zuto.com/blog/how-to-improve-credit-score/" role="link">
              Read our advice on how to improve your credit score
            </Link>
            &nbsp;– it can be as simple as registering on the electoral roll. We work with a number
            of lenders who offer&nbsp;
            <Link href="https://www.zuto.com/bad-credit-car-finance/" role="link">
              bad credit car finance
            </Link>
            .
          </p>
        </>
      )
    },
    {
      subHeading: 'Search for a car to buy',
      content: (
        <>
          <p>
            We work with an approved network of trusted dealerships across the UK so you can search
            from over {VehicleVariables.numberOfVehicles} vehicles in your MyZuto account area after
            you&apos;ve applied. We&apos;re also&nbsp;
            <Link href="https://www.autotrader.co.uk/cars/finance/calculator" role="link">
              AutoTrader&apos;s
            </Link>
            &nbsp;official car finance partner.
          </p>
        </>
      )
    },
    {
      subHeading: 'Talk to our team',
      content: (
        <>
          <p>
            We have a team dedicated to helping our customers, from finding the right car finance
            product for you to helping you pick a car. Call us on&nbsp;
            {ContactVariables.zutoContactNumber} or&nbsp;
            <Link href="https://www.zuto.com/contact-us/" role="link">
              use our contact form
            </Link>
            &nbsp;to request a call-back.
          </p>
        </>
      )
    }
  ]
}

export const carCostAccordionData = [
  {
    title: 'Fuel',
    content: (
      <p>
        <span>
          For many years, diesel was cheaper to buy than petrol and had greater fuel economy,
          leading it to be the fuel of choice for anyone driving high mileages (such as taxi drivers
          or sales reps). However, in more recent years, petrol has been cheaper to buy and fuel
          efficiency has much improved.
        </span>
        <br />
        <span>
          Diesel has also had somewhat of a bad press recently as it&apos;s not the most
          eco-friendly of fuels and there&apos;s talk of diesel cars being banned from many major
          cities in years to come.
        </span>
      </p>
    )
  },
  {
    title: 'Miles per gallon (MPG)',
    content: (
      <p>
        <span>
          Regardless of whether you purchase a petrol or diesel car, (or indeed a hybrid), the most
          important consideration isn&apos;t actually the cost of the fuel itself, but how many
          miles you get for each gallon of fuel that you purchase. In general, the smaller and more
          efficient the car, the higher the miles you will get to the gallon, thus spending less on
          fuel.&nbsp;This type of information is easy to research by looking at a car&apos;s
          specifications online.
        </span>
      </p>
    )
  },
  {
    title: 'Servicing and maintenance',
    content: (
      <p>
        <span>
          No matter how good a car you buy, it will need repairing at some point. Whether you choose
          to use main dealer servicing and repairs or independents you may find that some makes of
          car are more expensive to repair and maintain than others.
        </span>
        <br />
        <span>
          If you are considering purchasing a particular brand of car and want to keep a tight rein
          over future repair costs, it is worth inquiring about how much regular services cost and
          how much typical repairs are.&nbsp;If you want to have an even tighter rein over future
          repair costs, then it may be worth inquiring about an extended warranty. These are
          available from the manufacturer or third party providers and, for one yearly cost, cover
          you for a range of potential repairs.
        </span>
      </p>
    )
  },
  {
    title: 'MOT',
    content: (
      <p>
        <span>
          Cars older than three years need an annual MOT. The cost of a car MOT is capped by the
          government at £54.85.&nbsp;
        </span>
      </p>
    )
  },
  {
    title: 'Tax',
    content: (
      <p>
        <span>
          For used cars made before the end of March 2017, road tax can go from £0 to £535 per year.
          Newer cars produced after March 31, 2017, can be taxed at rates from £0 to £2000, so
          it&apos;s essential that you find out what exactly you will be paying and assess whether
          you can afford it. You can check car tax rates on the&nbsp;
        </span>
        <Link href="https://www.gov.uk/vehicle-tax-rate-tables" role="link">
          Gov.uk website
        </Link>
        <span>.</span>
      </p>
    )
  },
  {
    title: 'Insurance',
    content: (
      <p>
        <span>
          Insurance can vary significantly, depending on the car you choose, your driving history,
          where you live, and how you&apos;ll use your car. Car insurance companies now work to a 50
          mark system, rather than the previous 20 level system. Group 1 is the cheapest group and
          50 is the most expensive.
        </span>
        <br />
        <span>
          Knowing that the number of categories have changed is really important to remember; you
          don&apos;t want to be caught out thinking that a car in the Group 15 category is in the
          bottom third of insurance groups rather than the top quarter. Generally speaking, smaller,
          less powerful cars are in the lower insurance groups with high powered, sporty, and
          exclusive cars being in the higher groups.
        </span>
      </p>
    )
  }
]

export const carFinanceAccordionData = [
  {
    title: 'How much can I borrow with Zuto?',
    content: (
      <>
        <p>
          When applying for finance through Zuto, we&apos;ll perform a&nbsp;
          <Link href="https://www.zuto.com/car-finance-eligibility/" role="link">
            soft credit check
          </Link>
          &nbsp;which reviews your income and affordability, and reviews your credit history,
          determining how much you can borrow and what sort of rates you may be offered. Typically,
          our lenders will offer anything from £2,000 to £50,000, but feel free to{' '}
          <Link href="https://www.zuto.com/contact-us/" role="link">
            get in touch over the phone&nbsp;
          </Link>
          if you would like to borrow more, and we&apos;ll see how we could help.
        </p>
        <p>
          One thing to consider is the vehicle you choose to finance; depending on the age and
          condition of the vehicle, you may be required to put a deposit down, which means
          you&apos;ll borrow less against the vehicle. If this is the case, our team will explain
          everything to you and discuss your options in detail.
        </p>
      </>
    )
  },
  {
    title: 'How much does car finance cost?',
    content: (
      <p>
        Your car finance payments will depend on the value of the vehicle you&apos;re hoping to
        drive, as well as factors such as your credit rating and how long you want the repayment
        period to be. However, the&nbsp;
        <Link href="https://www.zuto.com/help-and-advice/interest-rates/" role="link">
          interest rates
        </Link>
        &nbsp; vary from as low as 9% APR to 19%+ depending on your credit history.
      </p>
    )
  },
  {
    title: 'Can I get a car loan if I am self-employed?',
    content: (
      <p>
        We work with a number of lenders on our panel who can offer finance to people who are
        self-employed. You may need to provide additional documentation to the lender that shows
        your income - you can find out more about{' '}
        <Link href="https://www.zuto.com/self-employed-car-finance/" role="link">
          self employed car finance here
        </Link>
        . We work with hundreds of trusted dealers across the UK so whether you&apos;re looking for
        a car for home or a van for work, we may be able to help with that too.
      </p>
    )
  },
  {
    title: 'Do I have to put down a deposit?',
    content: (
      <p>
        No deposit car finance options are available which means an upfront cash payment is not
        always required. However, putting down a deposit will reduce the amount you need to borrow,
        which would mean your repayments would be less or you may be able to take the finance over a
        shorter period of time.
      </p>
    )
  },
  {
    title: 'Which type of car finance should I choose?',
    content: (
      <>
        <p>When applying for car finance, you&apos;ll have three main options:</p>
        <ul>
          <li>
            <Link href="https://www.zuto.com/hp-car-finance/" role="link">
              Hire Purchase finance
            </Link>
            : this involves making regular monthly payments, while the finance is secured against
            the vehicle. Our car HP calculator above will give you an indication of the type of car
            you could be eligible for. One of the benefits of this type of finance is that
            you&apos;ll know your monthly payments in advance, and there is no &apos;balloon&apos;
            fee at the end.&nbsp;
          </li>
          <li>
            <Link href="https://www.zuto.com/pcp-car-finance/" role="link">
              Personal Contract Purchase
            </Link>
            : this involves regular monthly payments and the finance is secured against the vehicle
            but you only pay off the depreciation. You can hand the keys back at the end of the
            contract period, or make a balloon payment to keep it.&nbsp;
          </li>
          <li>
            Personal Loan: this involves regular monthly payments, but the loan isn&apos;t secured
            against the vehicle.
          </li>
        </ul>
      </>
    )
  },
  {
    title: 'What happens next when I apply?',
    content: (
      <>
        <p>
          When looking at your car finance options, we follow a straight forward process (you can find out more about 
          {' '}<Link href="https://www.zuto.com/how-zuto-works/" role="link">
            how our process works here
          </Link>)
          . When you apply, we&apos;ll run an initial soft
          credit check on your profile (which won&apos;t impact your score) to make a preliminary
          decision on your eligibility.
        </p>
        <p>
          We&apos;ll then compare the finance options offered by our panel of lenders, to find the
          deal that suits your circumstances best. Your car expert will then get in touch to talk
          through your quote, how much you can borrow, and what kind of car you want to drive.
        </p>
      </>
    )
  },
  {
    title: 'How much is average car finance?',
    content: (
      <>
        <p>
          The average cost of car finance can vary from person to person. It can be affected by
          things such as whether you have a deposit available or a part exchange, and what your
          credit score looks like. The length of time you want to borrow the money for will also
          affect how much your monthly repayments will be. Using our calculator will allow you to
          change the term and credit score banding so you can see how much you could borrow.
        </p>
      </>
    )
  },
  {
    title: 'How do you decide the APR?',
    content: (
      <p>
        When applying for car finance, your payment will vary not just depending on the car&apos;s
        value and the repayment period, but also the Annual Percentage Rate or APR. The APR
        you&apos;ll pay is based on your personal circumstances and how you&apos;ve handled credit
        in the past. You&apos;ll generally pay a lower APR - less interest - the better your credit
        score is. Those with bad credit scores will usually pay higher interest rates, although that
        doesn&apos;t mean they&apos;re excluded from car finance. Find out more about <Link href="https://www.zuto.com/bad-credit-car-finance/" role="link">car finance for bad credit here</Link>.
      </p>
    )
  }
]

export const usefulInformationCardData = [
  {
    title: 'How to get car finance with bad credit',
    description:
      'If you have a low credit score, you may still be able to get car finance with one of our lenders - find out more with our bad credit finance guide.',
    buttonText: 'Bad credit car finance',
    buttonLink: 'https://www.zuto.com/bad-credit-car-finance/',
    buttonId: 'BadCreditCarFinanceCTA'
  },
  {
    title: 'Applying for finance on a used car',
    description:
      'Car finance can make getting a second-hand car more affordable. Read our guide to find out how to apply for used car finance.',
    buttonText: 'Used car finance',
    buttonLink: 'https://www.zuto.com/used-car-finance/',
    buttonId: 'UsedCarFinanceCTA'
  },
  {
    title: 'Considering the switch to electric?',
    description:
      "If you're thinking of making the switch to electric, read our guide which explains the ins and outs of electric car finance.",
    buttonText: 'Electric car finance',
    buttonLink: 'https://www.zuto.com/electric-car-finance/',
    buttonId: 'ElectricCarFinanceCTA'
  }
]
